import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { GridSortModel, GridPaginationModel, GridFilterModel } from '@mui/x-data-grid';
import type { PaginatedResponse, PaginationParams } from '../api/config';
import { useDebounce } from './useDebounce';

interface UsePaginatedQueryOptions<TData, TFilters> {
  queryKey: readonly unknown[];
  queryFn: (filters: TFilters, pagination: PaginationParams) => Promise<PaginatedResponse<TData>>;
  filters: TFilters;
  defaultPageSize?: number;
  defaultSort?: { field: string; sort: 'asc' | 'desc' };
  enabled?: boolean;
  staleTime?: number;
  /** Poll interval (ms). Omit to disable polling (default). */
  refetchInterval?: number;
  /** Keep polling while the tab is backgrounded. Only meaningful with refetchInterval. */
  refetchIntervalInBackground?: boolean;
}

/** Convert DataGrid's GridFilterModel to flat query params for the backend. */
function filterModelToParams(model: GridFilterModel): Pick<PaginationParams, 'filter_field' | 'filter_op' | 'filter_value'> {
  const item = model.items[0]; // Community edition: single filter
  if (!item?.field || !item?.operator) return {};
  // isEmpty/isNotEmpty don't need a value
  if (item.operator === 'isEmpty' || item.operator === 'isNotEmpty') {
    return { filter_field: item.field, filter_op: item.operator };
  }
  // All other operators require a non-empty value
  if (item.value === undefined || item.value === null || item.value === '') return {};
  return {
    filter_field: item.field,
    filter_op: item.operator,
    filter_value: String(item.value),
  };
}

export function usePaginatedQuery<TData, TFilters = Record<string, unknown>>({
  queryKey,
  queryFn,
  filters,
  defaultPageSize = 50,
  defaultSort,
  enabled = true,
  staleTime,
  refetchInterval,
  refetchIntervalInBackground,
}: UsePaginatedQueryOptions<TData, TFilters>) {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0, // MUI DataGrid uses 0-based pages
    pageSize: defaultPageSize,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>(
    defaultSort ? [defaultSort] : []
  );

  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const debouncedFilterModel = useDebounce(filterModel, 300);

  // Reset to page 0 when filters change.
  // Callers MUST wrap their filters object in useMemo to avoid spurious resets —
  // JSON.stringify is used here for deep comparison since the effect needs a
  // primitive dependency, but it assumes stable key ordering.
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    setPaginationModel(prev => prev.page === 0 ? prev : { ...prev, page: 0 });
  }, [filtersKey]);

  // Reset to page 0 when the debounced column filter changes
  const debouncedFilterKey = JSON.stringify(debouncedFilterModel);
  useEffect(() => {
    setPaginationModel(prev => prev.page === 0 ? prev : { ...prev, page: 0 });
  }, [debouncedFilterKey]);

  // Build pagination params (API uses 1-based pages)
  const filterParams = filterModelToParams(debouncedFilterModel);
  const paginationParams: PaginationParams = {
    page: paginationModel.page + 1,
    per_page: paginationModel.pageSize,
    sort_by: sortModel[0]?.field,
    sort_dir: sortModel[0]?.sort ?? undefined,
    ...filterParams,
  };

  // Include filters in the query key automatically so callers don't need to
  // duplicate filter values in queryKey. filtersKey is already a stable string.
  const fullQueryKey = [
    ...queryKey,
    filtersKey,
    paginationParams.page,
    paginationParams.per_page,
    paginationParams.sort_by,
    paginationParams.sort_dir,
    debouncedFilterKey,
  ];

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: fullQueryKey,
    queryFn: () => queryFn(filters, paginationParams),
    placeholderData: (previousData: PaginatedResponse<TData> | undefined) => previousData,
    enabled,
    staleTime,
    refetchInterval,
    refetchIntervalInBackground,
  });

  const onSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, []);

  return {
    rows: data?.data ?? [],
    rowCount: data?.pagination?.total_count ?? 0,
    isLoading,
    isFetching,
    error,
    paginationModel,
    onPaginationModelChange: setPaginationModel,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange: setFilterModel,
  };
}
